// "Make the proposal": the Standard Painting Proposal for this option (a maintenance
// contract: its tier's Maintenance Proposal, #515), built
// on the server from CRM (PRICING.md "The quote document") and saved to the
// job's Proposal & Take-Off folder. It goes to the client before they accept,
// so it writes nothing to the opportunity.
//
// It lives only on SharePoint and opens from there in a new tab (Word for the
// web) — never a copy on the device (DD, 2026-10-19). A save that fails says
// why; making it again once that's resolved is the way round it.
import { useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { Button } from '@higgins/ui';
import { ApiError, isConnectionFailure, makeProposalApi } from '../lib/api';
import { openWaitingTab } from '../lib/file-handoff';
import { isoToday } from '../lib/dates';
import { SavedDocumentResult, type SavedResult } from './saved-document-result';

interface Props {
  optionId: string;
  /** Why the proposal can't be made right now (REVIEW, unsynced changes); null = it can. */
  blockedBecause: string | null;
  /** What the document is, when it isn't the Standard Painting Proposal. */
  about?: string;
}

const STANDARD = 'The Standard Painting Proposal for this option, in Word: cover letter, scope per area and the payment summary filled in. It opens from the job’s SharePoint folder; finish the rest in Word.';

function describe(err: unknown): string {
  if (isConnectionFailure(err)) return 'Making the proposal needs signal.';
  if (err instanceof ApiError) return err.message;
  return 'The proposal could not be made.';
}

export function ProposalPanel({ optionId, blockedBecause, about }: Props) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SavedResult | null>(null);

  async function make() {
    setBusy(true);
    setResult(null);
    const tab = openWaitingTab('Making the proposal…');
    try {
      const proposal = await makeProposalApi(optionId, isoToday());
      if (proposal.savedUrl) {
        if (tab) tab.show(proposal.savedUrl);
        const where = tab ? tab.where : 'open it below';
        setResult({ ok: true, text: `${proposal.fileName} — saved to the job's Proposal & Take-Off folder and ${where}.`, url: proposal.savedUrl });
        return;
      }
      tab?.close();
      setResult({ ok: false, text: `The proposal wasn't saved to the job: ${proposal.saveProblem ?? 'SharePoint did not take it.'} Make it again once that's sorted.` });
    } catch (err) {
      tab?.close();
      setResult({ ok: false, text: describe(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 border-t border-slate-200 pt-3">
      <h4 className="mb-1 font-medium text-slate-900">Proposal for the client</h4>
      <p className="mb-2 text-xs text-slate-500">
        {about ?? STANDARD}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => void make()} disabled={busy || !!blockedBecause}>
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileText className="mr-1 h-4 w-4" />} {busy ? 'Making the proposal…' : 'Make the proposal'}
        </Button>
      </div>
      {blockedBecause && <p className="mt-1 text-xs text-slate-500">{blockedBecause}</p>}
      <SavedDocumentResult result={result} />
    </div>
  );
}
